import { GoogleMap } from "@react-google-maps/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGoogleMaps } from "../../../../hooks/useGoogleMaps";

type LatLngLiteral = google.maps.LatLngLiteral;

export type GoogleMapCapture = {
  imageUrl: string;
  name: string;
  widthMeters: number;
  heightMeters: number;
};

type GoogleMapBackgroundDialogProps = {
  isOpen: boolean;
  projectCoordinates?: string | null;
  canvasAspectRatio: number;
  onClose: () => void;
  onCapture: (capture: GoogleMapCapture) => void;
};

const DEFAULT_CENTER: LatLngLiteral = { lat: 46.4983, lng: 11.3548 };
const DEFAULT_ZOOM = 19;
const TILE_SIZE = 256;
const MAX_STATIC_MAP_SIZE = 640;

const parseProjectCoordinates = (coordinates?: string | null): LatLngLiteral | null => {
  if (!coordinates) return null;

  const parts = coordinates.split(",").map((value) => Number(value.trim()));
  const latValue = parts[0];
  const lngValue = parts[1];
  if (
    typeof latValue !== "number" ||
    typeof lngValue !== "number" ||
    !Number.isFinite(latValue) ||
    !Number.isFinite(lngValue)
  ) {
    return null;
  }

  return { lat: latValue, lng: lngValue };
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getCaptureSize = (containerSize: { width: number; height: number }, aspectRatio: number) => {
  const safeAspectRatio = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 16 / 9;
  const maxWidth = Math.min(containerSize.width * 0.8, MAX_STATIC_MAP_SIZE);
  const maxHeight = Math.min(containerSize.height * 0.8, MAX_STATIC_MAP_SIZE);

  let width = maxWidth;
  let height = width / safeAspectRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * safeAspectRatio;
  }

  return {
    width: Math.max(1, Math.round(width)),
    height: Math.max(1, Math.round(height)),
  };
};

const getLatLngAtPixelOffset = (center: LatLngLiteral, zoom: number, offsetX: number, offsetY: number): LatLngLiteral => {
  const scale = 2 ** Math.round(zoom);
  const siny = clamp(Math.sin((center.lat * Math.PI) / 180), -0.9999, 0.9999);
  const centerWorldX = ((center.lng + 180) / 360) * TILE_SIZE * scale;
  const centerWorldY =
    (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)) * TILE_SIZE * scale;

  const worldX = centerWorldX + offsetX;
  const worldY = centerWorldY + offsetY;
  const lng = (worldX / (TILE_SIZE * scale)) * 360 - 180;
  const mercatorY = Math.PI - (2 * Math.PI * worldY) / (TILE_SIZE * scale);
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(mercatorY) - Math.exp(-mercatorY)));

  return { lat, lng };
};

const getCaptureMeters = (center: LatLngLiteral, zoom: number, width: number, height: number) => {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const west = getLatLngAtPixelOffset(center, zoom, -halfWidth, 0);
  const east = getLatLngAtPixelOffset(center, zoom, halfWidth, 0);
  const north = getLatLngAtPixelOffset(center, zoom, 0, -halfHeight);
  const south = getLatLngAtPixelOffset(center, zoom, 0, halfHeight);

  return {
    widthMeters: google.maps.geometry.spherical.computeDistanceBetween(west, east),
    heightMeters: google.maps.geometry.spherical.computeDistanceBetween(north, south),
  };
};

export function GoogleMapBackgroundDialog({
  isOpen,
  projectCoordinates,
  canvasAspectRatio,
  onClose,
  onCapture,
}: GoogleMapBackgroundDialogProps) {
  const { isLoaded, error } = useGoogleMaps();
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapShellRef = useRef<HTMLDivElement | null>(null);
  const projectCenter = useMemo(() => parseProjectCoordinates(projectCoordinates), [projectCoordinates]);
  const [mapCenter, setMapCenter] = useState<LatLngLiteral>(projectCenter ?? DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [containerSize, setContainerSize] = useState({ width: 760, height: 520 });

  useEffect(() => {
    if (projectCenter) {
      setMapCenter(projectCenter);
    }
  }, [projectCenter]);

  useEffect(() => {
    if (!isOpen || !mapShellRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setContainerSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(mapShellRef.current);

    return () => observer.disconnect();
  }, [isOpen]);

  const captureSize = useMemo(
    () => getCaptureSize(containerSize, canvasAspectRatio),
    [canvasAspectRatio, containerSize],
  );

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    map.setMapTypeId(google.maps.MapTypeId.SATELLITE);
    map.moveCamera({ center: projectCenter ?? DEFAULT_CENTER, zoom: DEFAULT_ZOOM });

    if (!projectCenter && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(({ coords }) => {
        const center = { lat: coords.latitude, lng: coords.longitude };
        setMapCenter(center);
        map.moveCamera({ center });
      });
    }
  };

  const syncMapState = () => {
    const map = mapRef.current;
    const center = map?.getCenter();
    const zoom = map?.getZoom();

    if (center) {
      setMapCenter({ lat: center.lat(), lng: center.lng() });
    }

    if (typeof zoom === "number") {
      setMapZoom(Math.round(zoom));
    }
  };

  const handleCapture = () => {
    const map = mapRef.current;
    const center = map?.getCenter();
    const zoom = Math.round(map?.getZoom() ?? mapZoom);
    const captureCenter = center ? { lat: center.lat(), lng: center.lng() } : mapCenter;
    const { widthMeters, heightMeters } = getCaptureMeters(
      captureCenter,
      zoom,
      captureSize.width,
      captureSize.height,
    );
    const params = new URLSearchParams({
      center: `${captureCenter.lat},${captureCenter.lng}`,
      zoom: String(zoom),
      size: `${captureSize.width}x${captureSize.height}`,
      scale: "2",
      maptype: "satellite",
      format: "png",
      key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    });

    onCapture({
      imageUrl: `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`,
      name: `Google Map ${captureCenter.lat.toFixed(6)}, ${captureCenter.lng.toFixed(6)}`,
      widthMeters,
      heightMeters,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-5 py-5">
      <div className="flex h-[min(760px,calc(100vh-40px))] w-[min(920px,calc(100vw-40px))] flex-col overflow-hidden rounded-[6px] border border-[rgba(74,58,31,0.18)] bg-[rgba(245,240,230,0.98)] shadow-[0_24px_54px_rgba(20,16,8,0.24)]">
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-[rgba(74,58,31,0.12)] px-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#4b3e27]">Google Map</div>
            <div className="text-[11px] text-[#6c5b3d]">Move and zoom the map, then capture the selected area.</div>
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[rgba(74,58,31,0.16)] bg-white text-[#1f2937] transition active:scale-110"
            aria-label="Close Google Map"
            title="Close"
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
              <path d="M6 6l12 12M18 6 6 18" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div ref={mapShellRef} className="relative min-h-0 flex-1 bg-[#d7d2c7]">
          {!isLoaded || error ? (
            <div className="absolute inset-0 flex items-center justify-center text-[12px] font-medium text-[#4b3e27]">
              {error ? "Google Map non disponibile." : "Caricamento Google Map..."}
            </div>
          ) : (
            <>
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                zoom={DEFAULT_ZOOM}
                center={projectCenter ?? DEFAULT_CENTER}
                onLoad={handleMapLoad}
                onIdle={syncMapState}
                options={{
                  mapTypeId: google.maps.MapTypeId.SATELLITE,
                  streetViewControl: false,
                  fullscreenControl: false,
                  mapTypeControl: true,
                  rotateControl: false,
                  tilt: 0,
                }}
              />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div
                  className="border-2 border-[#d9480f] bg-[#d9480f]/10 shadow-[0_0_0_9999px_rgba(0,0,0,0.20)]"
                  style={{ width: captureSize.width, height: captureSize.height }}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex min-h-[54px] shrink-0 items-center justify-between gap-3 border-t border-[rgba(74,58,31,0.12)] px-4 py-2">
          <div className="text-[11px] font-medium text-[#4b3e27]">
            Area {captureSize.width}x{captureSize.height}px · zoom {mapZoom}
          </div>
          <button
            type="button"
            disabled={!isLoaded || Boolean(error)}
            className="inline-flex h-9 items-center justify-center rounded-md border border-[rgba(29,92,171,0.24)] bg-[#1d5cab] px-4 text-[11px] font-semibold text-white transition active:scale-110 disabled:cursor-not-allowed disabled:opacity-45"
            onClick={handleCapture}
          >
            Screenshot
          </button>
        </div>
      </div>
    </div>
  );
}
